import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { When } from 'react-if'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsSpan, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage'
import { formatTitleLogo } from '@/services/ContentCatalog'
import { options } from '@/services/ContentfulService'
import { isGuildSignupVideoLayout } from '@/services/GuildSignupPageService/validations'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { AlreadyAGuildMember } from '@/views/GuildSignupView/GuildSingleShowLayout/GuildHeroCTA/AlreadyAGuildMember'

interface GuildVideoHeroProps {
  hasUser: boolean
  hideAlreadyAGuildMember: boolean
}
export const GuildVideoHero: React.FC<GuildVideoHeroProps> = ({ hasUser, hideAlreadyAGuildMember }) => {
  const { layout, catalogTitle, experiments } = useGuildSignupContext()
  const titleLogo = formatTitleLogo(catalogTitle)

  if (!isGuildSignupVideoLayout(layout)) {
    return null
  }

  const videoLayout = layout
  const showMobileImage = Boolean(experiments['video_hero_mobile_image'])

  return (
    <section aria-label={videoLayout.heading} className="2xl:max-h-auto relative min-h-screen w-full overflow-x-hidden">
      <div className="aspect-h-1 aspect-w-1 relative flex w-full bg-cover md:hidden">
        <>
          <When condition={showMobileImage}>
            <ResponsiveBackgroundImage src={layout.backgroundImageUrl ?? ''} />
            <div className="absolute inset-0 h-full w-full bg-gradient-to-b from-transparent to-core-gray-950" />
          </When>
          <When condition={!showMobileImage}>
            <video
              className="bg-cover object-cover md:h-full md:max-h-[800px] md:min-h-full"
              autoPlay
              playsInline
              muted
              loop
              src={layout.backgroundVideoUrl}
            />
            <div className="absolute inset-0 h-full w-full bg-gradient-to-b from-transparent to-core-gray-950" />
          </When>
        </>
      </div>
      <div className="relative hidden w-full bg-cover md:aspect-h-9 md:aspect-w-16 md:flex md:min-h-[800px]">
        <video
          className="bg-cover object-cover md:h-full md:max-h-[800px] md:min-h-full"
          autoPlay
          playsInline
          muted
          loop
          src={layout.backgroundVideoUrl}
        />
        <div className="absolute inset-0 h-full w-full bg-gradient-to-b from-transparent to-core-gray-950" />
      </div>
      <div className="absolute left-0 top-0 z-10 flex h-screen min-h-screen w-full flex-col bg-transparent px-4 py-16 md:max-h-[800px] md:px-20 md:py-32 xl:mx-[180px] xl:px-4">
        <div className="relative mt-auto flex w-full flex-col items-center justify-center gap-y-4 bg-transparent md:items-start md:gap-y-6">
          <div>
            <When condition={Boolean(catalogTitle)}>
              <div className="relative flex max-w-full items-center justify-center overflow-hidden md:justify-start">
                {titleLogo && (
                  <BoundedImage
                    className="mb-2 flex items-center justify-center md:justify-start"
                    alt={catalogTitle?.title}
                    path={titleLogo.cloudinaryPath}
                    width={600}
                    height={104}
                    sizes="(max-width: 640px) 358px, (max-width: 1024px) 600px, 600px"
                    imageClassName="h-auto max-h-[40px] w-auto max-w-full object-contain md:max-h-[80px] lg:max-h-[104px] lg:max-w-[600px]"
                  />
                )}
              </div>
            </When>
          </div>
          <div className="flex max-w-[600px] flex-col gap-y-4 se:max-w-full">
            <HeadingXS
              as={AsH1}
              weight="bold"
              className="se:photon-title-lg md:photon-heading-lg 2xl:photon-heading-xl w-full text-balance bg-transparent text-center md:text-left"
              color="white"
            >
              {videoLayout.heading}
            </HeadingXS>
            {videoLayout?.paragraph?.json && (
              <ParagraphSM
                as={AsSpan}
                className="[&>p]:photon-paragraph-sm md:[&>p]:photon-paragraph-md se:[&>p]:photon-caption-xs 2xl:[&>p]:photon-paragraph-xl mb-3 w-full text-balance bg-transparent text-center md:text-left"
                color="white"
              >
                {documentToReactComponents(videoLayout.paragraph.json, options)}
              </ParagraphSM>
            )}
          </div>

          <div className="flex w-full max-w-[600px] flex-col gap-4 pb-[48px] md:pb-[120px] se:pb-[32px]">
            <GuildEmailInput hasUser={hasUser} isCentered={false} className="w-full" inputContainerClassName="!px-0" />
            <AlreadyAGuildMember
              className={classNames('text-center text-[14px] text-gray-300 lg:text-left', {
                'hidden lg:block': hideAlreadyAGuildMember,
              })}
              linkClassName="text-core-oxide relative underline cursor-pointer"
            />
          </div>
        </div>
      </div>
      <div
        className="absolute inset-0 h-full w-[130%]"
        style={{ background: 'linear-gradient(180deg, rgba(20, 20, 20, 0.00) 0%, #222222 100%)' }}
      />
    </section>
  )
}
